import type { ReactNode } from 'react'
import { Button, RawButton } from '@/components/Button'
import { Dropdown, DropdownScroll } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import styles from './FilterBar.module.css'

interface FilterButtonProps {
  appliedFilterCount: number
  /**
   * The fields to filter by, usually `AvailableFilters`.
   */
  children: ReactNode
  testId?: string
}

/**
 * Toolbar button that lists the fields you can filter by. Picking one adds a
 * chip below the toolbar with its editor already open.
 */
export function FilterButton({
  appliedFilterCount: count,
  children,
  testId,
}: FilterButtonProps) {
  return (
    <Dropdown
      autoDismiss
      position="bottom-end"
      testId={testId}
      button={
        <Button
          label={count > 0 ? `Filter ${count}` : 'Filter'}
          size="small"
          iconLeft="filter"
        />
      }
    >
      <DropdownScroll>{children}</DropdownScroll>
    </Dropdown>
  )
}

interface AppliedFiltersProps {
  /**
   * The applied filters, as `FilterChip`s.
   */
  children: ReactNode
  onClearAll: () => void
  /**
   * Things to do with the applied filters, at the end of the row after the
   * clear button.
   */
  actions?: ReactNode
  testId?: string
}

/**
 * Applied filters as removable chips.
 */
export function AppliedFilters({
  children,
  onClearAll,
  actions,
  testId,
}: AppliedFiltersProps) {
  return (
    <div className={styles.filterRow}>
      {children}
      {actions && <span className={styles.filterRowSpacer} />}
      <Button
        label={actions ? 'Clear all' : 'Clear'}
        variant="text"
        size="small"
        onPress={onClearAll}
        testId={testId}
      />
      {actions}
    </div>
  )
}

interface FilterChipProps {
  field: string
  value: string
  isEditing: boolean
  onStartEditing: () => void
  onStopEditing: () => void
  onRemove: () => void
  /**
   * The filter's value editor, shown while editing.
   */
  children: ReactNode
  testId?: string
}

/**
 * An applied filter, showing its field and value; opens its editor when pressed.
 */
export function FilterChip({
  field,
  value,
  isEditing,
  onStartEditing,
  onStopEditing,
  onRemove,
  children,
  testId,
}: FilterChipProps) {
  return (
    <Dropdown
      isOpen={isEditing}
      allowOverflow
      compact
      onRequestClose={onStopEditing}
      position="bottom-start"
      button={
        <span className={styles.filterChip}>
          <RawButton
            className={styles.filterChipBody}
            onPress={onStartEditing}
            testId={testId}
          >
            <span className={styles.filterChipField}>{field}</span>
            <span className={styles.filterChipValue}>{value}</span>
          </RawButton>
          <RawButton
            className={styles.filterChipRemove}
            aria-label={`Remove ${field} filter`}
            onPress={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <Icon auto size={16} name="close" />
          </RawButton>
        </span>
      }
    >
      {children}
    </Dropdown>
  )
}
